import React, { useState } from "react";

/**
 * Wiederverwendbarer Upload mit optionaler Client-Vorverarbeitung
 * und der Möglichkeit, serverseitige Optionen mitzusenden.
 *
 * WICHTIG: Der Server ENTFORCED Größe/Format. Die Client-Verarbeitung ist optional.
 *
 * Props:
 * - uploadUrl: string                   // z.B. "/api/news/upload-image" oder "/api/upload"
 * - headers: Record<string,string>      // z.B. { Authorization: `Bearer ${token}` }
 * - fieldName: string = "image"         // Name des Datei-Felds
 * - accept: string = "image/*"
 * - maxSize?: number (Bytes)            // optionales Rohdatei-Limit (vor Verarbeitung)
 *
 * - enforceSize?: { width, height, mode?, format?, quality? }
 *   -> optional Client-Resize (cover/contain), Format (webp/jpeg/png), Qualität (0..1)
 *
 * - serverOptions?: { width?, height?, mode?, format?, quality?, prefix? }
 *   -> wird als FormData-Felder mitgeschickt (Server entscheidet final)
 *   -> format: "webp" | "jpeg" | "png" | "avif"
 *   -> quality: 1..100
 *   -> prefix: string (Name-Präfix)
 *
 * - value: string                       // aktuell gespeicherter Dateiname
 * - onChange(filename: string)
 * - onUploaded(result: any)
 * - onRemove()
 *
 * - previewUrl?: string
 * - buildPreviewUrl?: (filename: string) => string
 *
 * - className / classes / labels        // Styling/Texte frei anpassbar
 */
export default function UploadImage({
    uploadUrl = "/api/upload",
    headers = {},
    fieldName = "image",
    accept = "image/*",
    maxSize,

    enforceSize, // optional: clientseitige Bearbeitung

    serverOptions, // optional: serverseitige Optionen mitschicken

    value = "",
    onChange,
    onUploaded,
    onRemove,

    previewUrl = "",
    buildPreviewUrl,

    disabled = false,

    className = "uploader",
    classes = {
        dropzone: "uploader__dropzone",
        button: "uploader__button",
        progress: "uploader__progress",
        preview: "uploader__preview",
        filename: "uploader__filename",
        remove: "uploader__remove",
        hint: "uploader__hint",
    },
    labels = {
        title: "Datei-Upload",
        select: "Datei wählen",
        uploading: "Lade hoch…",
        remove: "Entfernen",
        hint: "",
    },
}) {
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);

    const computedPreview =
        previewUrl || (buildPreviewUrl && typeof value === "string" ? buildPreviewUrl(value) : "");

    // ---- optionale Client-Verarbeitung (Resize/Format) ----
    const blobFromDataURL = (dataURL) => {
        const [meta, data] = dataURL.split(",");
        const mime = (meta.match(/data:(.*?);base64/) || [])[1] || "image/webp";
        const bin = atob(data);
        const len = bin.length;
        const buf = new Uint8Array(len);
        for (let i = 0; i < len; i++) buf[i] = bin.charCodeAt(i);
        return new Blob([buf], { type: mime });
    };

    const processToFixedSize = async (file) => {
        if (!enforceSize) return file;
        const { width, height, mode = "cover", format = "image/webp", quality = 0.9 } = enforceSize;
        if (!width || !height) return file;

        const imgURL = URL.createObjectURL(file);
        try {
            const img = await new Promise((resolve, reject) => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.onerror = reject;
                image.src = imgURL;
            });

            const srcW = img.naturalWidth || img.width;
            const srcH = img.naturalHeight || img.height;
            if (!srcW || !srcH) return file;

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");

            if (format === "image/jpeg") {
                ctx.fillStyle = "#fff";
                ctx.fillRect(0, 0, width, height);
            }

            if (mode === "contain") {
                const scale = Math.min(width / srcW, height / srcH);
                const drawW = Math.round(srcW * scale);
                const drawH = Math.round(srcH * scale);
                const dx = Math.round((width - drawW) / 2);
                const dy = Math.round((height - drawH) / 2);
                ctx.drawImage(img, 0, 0, srcW, srcH, dx, dy, drawW, drawH);
            } else {
                const scale = Math.max(width / srcW, height / srcH);
                const sw = Math.round(width / scale);
                const sh = Math.round(height / scale);
                const sx = Math.max(0, Math.floor((srcW - sw) / 2));
                const sy = Math.max(0, Math.floor((srcH - sh) / 2));
                ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
            }

            const dataURL = canvas.toDataURL(format, quality);
            const outBlob = blobFromDataURL(dataURL);

            const ext = format === "image/webp" ? ".webp" : format === "image/png" ? ".png" : ".jpg";
            const base = (file.name || "image")
                .replace(/\.[a-z0-9]+$/i, "")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "");
            const outName = `${base}-${width}x${height}${ext}`;

            return new File([outBlob], outName, { type: outBlob.type });
        } finally {
            URL.revokeObjectURL(imgURL);
        }
    };

    // ---- Upload via XHR + Progress ----
    const doUpload = (file) =>
        new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", uploadUrl, true);
            Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
            };
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        resolve(JSON.parse(xhr.responseText));
                    } catch {
                        resolve({ ok: true });
                    }
                } else reject(new Error(xhr.responseText || `Upload failed (${xhr.status})`));
            };
            xhr.onerror = () => reject(new Error("Netzwerkfehler beim Upload"));

            const fd = new FormData();
            fd.append(fieldName, file);

            // optionale serverseitige Steuerung mitschicken (der Server erzwingt final)
            if (serverOptions && typeof serverOptions === "object") {
                const { width, height, mode, format, quality, prefix } = serverOptions;
                if (width) fd.append("width", String(width));
                if (height) fd.append("height", String(height));
                if (mode) fd.append("mode", String(mode));           // cover|contain|fill|inside|outside
                if (format) fd.append("format", String(format));     // webp|jpeg|png|avif
                if (quality) fd.append("quality", String(quality));  // 1..100
                if (prefix) fd.append("prefix", String(prefix));     // Dateiname-Präfix
            }

            xhr.send(fd);
        });

    const handleFiles = async (files) => {
        const file = files?.[0];
        if (!file || disabled) return;

        if (accept && accept !== "*/*" && !file.type.match(accept.replace("*", ".*"))) {
            alert(`Dateityp nicht erlaubt (${file.type}). Erlaubt: ${accept}`);
            return;
        }
        if (maxSize && file.size > maxSize) {
            alert(`Datei zu groß. Maximal ${Math.round(maxSize / 1024 / 1024)} MB.`);
            return;
        }

        setUploading(true);
        setProgress(0);

        try {
            const processed = enforceSize ? await processToFixedSize(file) : file;
            const result = await doUpload(processed); // { filename, url, ... }
            if (result?.filename && onChange) onChange(result.filename);
            if (onUploaded) onUploaded(result);
        } catch (e) {
            alert(e.message || "Upload fehlgeschlagen");
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    return (
        <div className={className} aria-disabled={disabled || uploading}>
            <div
                className={`${classes.dropzone} ${dragOver ? "is-dragover" : ""}`}
                role="button"
                tabIndex={0}
                aria-label={labels.title}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!disabled) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    if (!disabled) handleFiles(e.dataTransfer.files);
                }}
                onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !disabled) {
                        e.currentTarget.querySelector("input[type=file]")?.click();
                    }
                }}
            >
                <input
                    type="file"
                    accept={accept}
                    style={{ display: "none" }}
                    onChange={(e) => !disabled && handleFiles(e.target.files)}
                    aria-hidden="true"
                />
                <div className="uploader__row" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                        type="button"
                        className={classes.button}
                        onClick={(e) => e.currentTarget.closest("[role=button]").querySelector('input[type="file"]').click()}
                        disabled={disabled || uploading}
                    >
                        {uploading ? labels.uploading : labels.select}
                    </button>

                    {!!value && <span className={classes.filename}>Datei: {String(value)}</span>}

                    {!!value && onRemove && (
                        <button type="button" className={classes.remove} onClick={onRemove} disabled={disabled || uploading}>
                            {labels.remove}
                        </button>
                    )}
                </div>

                {uploading && (
                    <div className={classes.progress} role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                        <div className="uploader__progressBar" style={{ width: `${progress}%` }} />
                    </div>
                )}
            </div>

            {!!computedPreview && <img className={classes.preview} src={computedPreview} alt="Vorschau" />}

            {labels.hint && <small className={classes.hint}>{labels.hint}</small>}
        </div>
    );
}
